import { useEffect, useState } from "react";
import { MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function WhatsAppCard() {
  const { t } = useI18n();
  const [wa, setWa] = useState(null);
  const [mode, setMode] = useState(null); // null | "different" | "otp"
  const [num, setNum] = useState("");
  const [otp, setOtp] = useState("");

  const load = () => api.get("/applicator/whatsapp").then(({ data }) => setWa(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!wa) return null;

  const connectLogin = async () => { await api.post("/applicator/whatsapp/connect", { use_login_mobile: true }); toast.success(t("whatsapp_connected") || "WhatsApp connected"); setMode(null); load(); };
  const connectDifferent = async () => {
    if (num.replace(/\D/g, "").length < 10) return toast.error("Enter a valid number");
    await api.post("/applicator/whatsapp/connect", { use_login_mobile: false, whatsapp_mobile: num });
    toast.success(t("otp_sent") || "OTP sent"); setMode("otp");
  };
  const verify = async () => { try { await api.post("/applicator/whatsapp/verify", { mobile: num, code: otp }); toast.success(t("whatsapp_connected") || "WhatsApp connected"); setMode(null); setOtp(""); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const disconnect = async () => { if (!window.confirm(t("confirm_disconnect") || "Disconnect WhatsApp?")) return; await api.post("/applicator/whatsapp/disconnect"); toast.success("Disconnected"); load(); };

  return (
    <div className="hair-border bg-white/[0.02] p-5 mb-8" data-testid="whatsapp-card">
      <p className="errah-label gold-text mb-3 flex items-center gap-2"><MessageCircle size={14} /> WhatsApp</p>
      <div className="flex items-center gap-2 mb-4">
        {wa.whatsapp_connected
          ? <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400" data-testid="wa-status"><CheckCircle2 size={16} /> {t("connected") || "Connected"}{wa.whatsapp_mobile ? ` · ${wa.whatsapp_mobile}` : ""}</span>
          : <span className="text-sm text-gray-400" data-testid="wa-status">{t("not_connected") || "Not Connected"}</span>}
      </div>

      {!wa.whatsapp_connected && mode === null && (
        <div className="flex flex-wrap gap-2">
          <button onClick={connectLogin} data-testid="wa-connect-login" className="bg-[#C9A45C] text-black px-4 py-2.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">{t("use_verified_mobile") || "Use my verified mobile"} · {t("connect_whatsapp") || "Connect WhatsApp"}</button>
          <button onClick={() => setMode("different")} data-testid="wa-use-different" className="border border-white/20 px-4 py-2.5 text-sm hover:bg-white hover:text-black transition-colors">{t("different_number") || "Use a different number"}</button>
        </div>
      )}
      {mode === "different" && (
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="errah-label block mb-1">WhatsApp Number</label>
            <input value={num} onChange={(e) => setNum(e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="wa-number-input" placeholder="10-digit" className="bg-white/[0.03] border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-[#C9A45C]" /></div>
          <button onClick={connectDifferent} data-testid="wa-send-otp" className="bg-[#C9A45C] text-black px-4 py-2.5 text-sm font-semibold">{t("send_otp") || "Send OTP"}</button>
          <button onClick={() => setMode(null)} className="text-xs text-gray-400">Cancel</button>
        </div>
      )}
      {mode === "otp" && (
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="errah-label block mb-1">OTP for {num}</label>
            <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="wa-otp-input" className="bg-white/[0.03] border border-white/15 px-3 py-2.5 text-sm tracking-widest outline-none focus:border-[#C9A45C]" /></div>
          <button onClick={verify} data-testid="wa-verify" className="bg-[#C9A45C] text-black px-4 py-2.5 text-sm font-semibold">{t("verify") || "Verify"}</button>
        </div>
      )}

      {wa.whatsapp_connected && (
        <div className="flex gap-2">
          <button onClick={() => setMode("different")} data-testid="wa-change" className="border border-white/20 px-4 py-2 text-xs hover:bg-white hover:text-black transition-colors">{t("change_whatsapp") || "Change Number"}</button>
          <button onClick={disconnect} data-testid="wa-disconnect" className="border border-red-500/40 text-red-400 px-4 py-2 text-xs hover:bg-red-500/10 transition-colors">{t("disconnect") || "Disconnect"}</button>
        </div>
      )}
      {!wa.provider_configured && <p className="text-[11px] text-gray-600 mt-3">Note: WhatsApp messaging is not yet active on the platform. This connects your number for future updates.</p>}
    </div>
  );
}
